import { useCallback, useMemo } from 'react';
import { PADDING, SNAP_GRID } from '../constants';

/**
 * Hook for coordinate conversions between:
 * - Normalized (0-1): Motor bboxes
 * - Virtual (0-VIRTUAL_SIZE): Internal data model (rooms, walls)
 * - Layer (px): Konva canvas pixel coordinates
 *
 * The scale factor (SC) maps virtual units to pixels.
 * Example: if container is 800px wide and VIRTUAL_SIZE=1000,
 * SC = (800 - 2*PADDING) / VIRTUAL_SIZE ≈ 0.68
 */
export default function useCoordinates(containerWidth, containerHeight, extW, extH) {
  // Scale factor: how many pixels per virtual unit
  const sc = useMemo(() => {
    if (!containerWidth || !containerHeight || !extW || !extH) return 1;
    const availW = containerWidth - 2 * PADDING;
    const availH = containerHeight - 2 * PADDING;
    return Math.min(availW / extW, availH / extH);
  }, [containerWidth, containerHeight, extW, extH]);

  // Offset to center the plant in the canvas
  const offset = useMemo(() => {
    if (!containerWidth || !containerHeight || !extW || !extH) return { x: 0, y: 0 };
    const plantW = extW * sc;
    const plantH = extH * sc;
    return {
      x: (containerWidth - plantW) / 2,
      y: (containerHeight - plantH) / 2,
    };
  }, [containerWidth, containerHeight, extW, extH, sc]);

  // Virtual (data) → Layer (pixel)
  const dataToLayer = useCallback((dx, dy) => ({
    x: offset.x + dx * sc,
    y: offset.y + dy * sc,
  }), [offset, sc]);

  // Layer (pixel) → Virtual (data)
  const layerToData = useCallback((lx, ly) => ({
    x: (lx - offset.x) / sc,
    y: (ly - offset.y) / sc,
  }), [offset, sc]);

  // Scale a data value to pixels
  const s = useCallback((val) => val * sc, [sc]);

  // Snap to grid
  const snap = useCallback((val) => Math.round(val / SNAP_GRID) * SNAP_GRID, []);

  // Normalized (0-1) → Virtual coordinates
  const normalizedToVirtual = useCallback((bbox, extWall) => {
    const ew = extWall?.w || extW;
    const eh = extWall?.h || extH;
    return {
      ax: bbox.x * ew,
      ay: bbox.y * eh,
      aw: bbox.w * ew,
      ah: bbox.h * eh,
    };
  }, [extW, extH]);

  // Virtual → Normalized (0-1)
  const virtualToNormalized = useCallback((room, extWall) => {
    const ew = extWall?.w || extW;
    const eh = extWall?.h || extH;
    return {
      x: room.ax / ew,
      y: room.ay / eh,
      w: room.aw / ew,
      h: room.ah / eh,
    };
  }, [extW, extH]);

  return {
    sc,
    offset,
    dataToLayer,
    layerToData,
    s,
    snap,
    normalizedToVirtual,
    virtualToNormalized,
    PADDING,
  };
}
